import React, { FC, Suspense, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Col, Row } from 'reactstrap';
import { useFeatures } from 'utils/util/features';
import { useStateQueryParam } from 'utils/util/hooks';

import { AdvancedCurrentPeopleFilterContextProvider } from 'views/Widgets/Filters/AdvancedSpecializedFilter';
import * as consts from '../../consts/consts';
import Page from '../Layout/Pages/Page';
import SwitchInput from '../Widgets/Inputs/SwitchInput';
import Loading from '../Widgets/Loading';
import OrganizationAudit, {
  AuditEntryType,
} from './PeopleAdministration/Audit/OrganizationAudit';
import PeopleAdministrationAdministrators from './PeopleAdministrationAdministrators';

const PeopleAdministrationDashboard = React.lazy(
  () => import('./PeopleAdministrationDashboard')
);

const BOOL_URL_TO_VALUE_CONVERTER = (value) => value == 'true';
const BOOL_VALUE_TO_URL_CONVERTER = (value) => (value ? 'true' : 'false');

const PersonAdministrationHubPage: FC = () => {
  const [showAllColumns, setShowAllColumns] = useStateQueryParam<boolean>(
    'show_all_columns',
    false,
    BOOL_URL_TO_VALUE_CONVERTER,
    BOOL_VALUE_TO_URL_CONVERTER
  );
  const [
    hasAtLeastOneHideByDefaultColumn,
    setHasAtLeastOneHideByDefaultColumn,
  ] = useState(false);
  const { formatMessage } = useIntl();

  const { ifEnabled } = useFeatures();

  const tabs = [
    {
      path: consts.ADMINISTRATION_PEOPLE(formatMessage).path,
      name: (
        <FormattedMessage
          id="app.widgets.task.all_people"
          defaultMessage="All people"
        />
      ),
      shouldRenderContainer: !showAllColumns,
      containerClassName: showAllColumns ? 'container-fluid px-5' : undefined,
      content: (
        <Suspense
          fallback={
            <Loading
              message={formatMessage({
                id: 'app.views.administration.people_administration_hub_page.message.loading_dashboard',
                defaultMessage: 'Loading dashboard...',
              })}
            />
          }
        >
          <PeopleAdministrationDashboard
            showAllColumns={!!showAllColumns}
            setHasAtLeastOneHideByDefaultColumn={
              setHasAtLeastOneHideByDefaultColumn
            }
          />
        </Suspense>
      ),
      action: (
        <Row>
          {!hasAtLeastOneHideByDefaultColumn && (
            <Col className="col-auto me-sm-3 mb-3 mb-sm-0">
              <SwitchInput
                switchLabel={
                  <FormattedMessage
                    id="app.widgets.task.show_advanced_columns"
                    defaultMessage="Show advanced columns"
                  />
                }
                value={showAllColumns}
                onChange={setShowAllColumns}
              />
            </Col>
          )}
        </Row>
      ),
    },
    {
      path: consts.ADMINISTRATION_PEOPLE_ADMINISTRATORS(formatMessage).path,
      name: consts.ADMINISTRATION_PEOPLE_ADMINISTRATORS(formatMessage).name,
      content: (
        <AdvancedCurrentPeopleFilterContextProvider roleScope="SYSTEM_ADMIN">
          <PeopleAdministrationAdministrators />
        </AdvancedCurrentPeopleFilterContextProvider>
      ),
    },
    ...ifEnabled(
      'people_admin_admins_beta',
      [
        {
          path: consts.ADMINISTRATION_ORGANIZATION_AUDIT(formatMessage).path,
          name: consts.ADMINISTRATION_ORGANIZATION_AUDIT(formatMessage).name,
          content: (
            <OrganizationAudit
              auditEntryTypes={[AuditEntryType.ADMIN_MANAGEMENT]}
            />
          ),
        },
      ],
      []
    ),
  ];

  return (
    <Page
      title={consts.ADMINISTRATION_PEOPLE(formatMessage).name}
      tabs={tabs}
    />
  );
};

export default React.memo(PersonAdministrationHubPage);
